import { useEffect, useRef, useState } from 'react';
import { Rectangle, useMap } from 'react-leaflet';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { GeographicArea } from 'org-graph/geographicSelection';

interface Props {
  drawing: boolean;
  area: GeographicArea | null;
  onChange: (area: GeographicArea) => void;
  onCancel: () => void;
}

export default function MapAreaSelection({ drawing, area, onChange, onCancel }: Props) {
  const map = useMap();
  const { t } = useTranslation();
  const [draft, setDraft] = useState<L.LatLngBounds | null>(null);
  const start = useRef<{ point: L.Point; latlng: L.LatLng } | null>(null);
  const overlay = useRef<HTMLDivElement>(null);
  const commit = (bounds: L.LatLngBounds) => onChange({ south: bounds.getSouth(), west: bounds.getWest(), north: bounds.getNorth(), east: bounds.getEast() });

  useEffect(() => {
    if (!drawing) return;
    overlay.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      start.current = null;
      setDraft(null);
    };
  }, [drawing, onCancel]);

  const bounds = draft || (area ? L.latLngBounds([area.south, area.west], [area.north, area.east]) : null);
  return <>
    {bounds && <Rectangle bounds={bounds} interactive={false} pathOptions={{ color: '#2563eb', weight: 2, fillOpacity: 0.12, dashArray: '6 4' }} />}
    {drawing && <Box ref={overlay} tabIndex={-1} role="region" aria-label={t('ui.map.drawArea')}
      sx={{ position: 'absolute', inset: 0, zIndex: 1100, cursor: 'crosshair', touchAction: 'none', outline: 'none' }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        start.current = { point: L.point(event.clientX, event.clientY), latlng: map.mouseEventToLatLng(event.nativeEvent) };
        setDraft(null);
      }}
      onPointerMove={(event) => {
        if (start.current) setDraft(L.latLngBounds(start.current.latlng, map.mouseEventToLatLng(event.nativeEvent)));
      }}
      onPointerUp={(event) => {
        if (!start.current) return;
        const first = start.current;
        start.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        if (first.point.distanceTo(L.point(event.clientX, event.clientY)) >= 8) commit(L.latLngBounds(first.latlng, map.mouseEventToLatLng(event.nativeEvent)));
        setDraft(null);
      }}
      onPointerCancel={() => { start.current = null; setDraft(null); }}>
      <Paper onPointerDown={event => event.stopPropagation()} sx={{ position: 'absolute', bottom: 24, left: 12, right: 12, p: 1.5, cursor: 'default' }}>
        <Typography variant="body2">{t('ui.map.drawAreaHelp')}</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="contained" onClick={() => commit(map.getBounds())}>{t('ui.map.selectViewport')}</Button>
          <Button size="small" onClick={onCancel}>{t('common.actions.cancel')}</Button>
        </Box>
      </Paper>
    </Box>}
  </>;
}
